import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import {
  deleteNotification,
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../api/notifications";
import "./NotificationBell.css";

const POLL_INTERVAL_MS = 60000;

const ICONS = {
  megaphone: "fa-solid fa-bullhorn",
  check: "fa-solid fa-circle-check",
  info: "fa-solid fa-circle-info",
  bell: "fa-solid fa-bell",
};

/**
 * Links an administrator types can point anywhere. Same-origin ones are routed
 * in-app; anything else stays a real external link in a new tab.
 */
const toInternalPath = (url) => {
  if (!url) return null;
  if (url.startsWith("/")) return url;

  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : null;
  } catch {
    return null;
  }
};

const relativeTime = (value, isArabic) => {
  if (!value) return "";

  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";

  const minutes = Math.round((Date.now() - then) / 60000);
  const locale = isArabic ? "ar" : "en";
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (minutes < 1) return formatter.format(0, "minute");
  if (minutes < 60) return formatter.format(-minutes, "minute");

  const hours = Math.round(minutes / 60);
  if (hours < 24) return formatter.format(-hours, "hour");

  return formatter.format(-Math.round(hours / 24), "day");
};

/**
 * `variant="inline"` is for the mobile drawer: the drawer is a transformed
 * ancestor, which would make a fixed/absolute panel position against it rather
 * than the viewport, so there the panel simply expands in flow instead.
 */
const NotificationBell = ({ onNavigate, variant = "floating" }) => {
  const { user } = useAuth();
  const { t, isArabic } = useLanguage();

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const containerRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      setUnread(await fetchUnreadCount());
    } catch {
      /* A failed poll is not worth surfacing; the next one will correct it. */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      const data = await fetchNotifications();
      setItems(data.data ?? []);
      setUnread(data.unread_count ?? 0);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  /* Poll the cheap count endpoint, and only while the tab is actually visible. */
  useEffect(() => {
    if (!user) {
      setItems([]);
      setUnread(0);
      setOpen(false);
      return undefined;
    }

    refreshCount();

    const tick = () => {
      if (document.visibilityState === "visible") refreshCount();
    };

    const timer = setInterval(tick, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", tick);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [user, refreshCount]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!user) return null;

  const togglePanel = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const handleRead = async (notification) => {
    if (notification.read_at) return;

    setItems((current) =>
      current.map((item) =>
        item.id === notification.id ? { ...item, read_at: new Date().toISOString() } : item
      )
    );
    setUnread((current) => Math.max(0, current - 1));

    try {
      const data = await markNotificationRead(notification.id);
      setUnread(data.unread_count ?? 0);
    } catch {
      loadList();
    }
  };

  const handleReadAll = async () => {
    setItems((current) =>
      current.map((item) => (item.read_at ? item : { ...item, read_at: new Date().toISOString() }))
    );
    setUnread(0);

    try {
      await markAllNotificationsRead();
    } catch {
      loadList();
    }
  };

  const handleDelete = async (event, id) => {
    event.stopPropagation();

    const removed = items.find((item) => item.id === id);
    setItems((current) => current.filter((item) => item.id !== id));
    if (removed && !removed.read_at) setUnread((current) => Math.max(0, current - 1));

    try {
      const data = await deleteNotification(id);
      setUnread(data.unread_count ?? 0);
    } catch {
      loadList();
    }
  };

  const badge = unread > 99 ? "99+" : String(unread);

  return (
    <div className={`hpa-notifications hpa-notifications--${variant}`} ref={containerRef}>
      <button
        type="button"
        className="hpa-notifications__trigger"
        onClick={togglePanel}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={
          unread > 0
            ? t("Notifications ({count} unread)", { count: unread })
            : t("Notifications")
        }
      >
        <i className="fa-regular fa-bell" aria-hidden="true" />
        {unread > 0 && <span className="hpa-notifications__badge">{badge}</span>}
      </button>

      {open && (
        <div className="hpa-notifications__panel" role="dialog" aria-label={t("Notifications")}>
          <div className="hpa-notifications__header">
            <strong>{t("Notifications")}</strong>
            {unread > 0 && (
              <button type="button" className="hpa-notifications__link" onClick={handleReadAll}>
                {t("Mark all as read")}
              </button>
            )}
          </div>

          <div className="hpa-notifications__list">
            {loading && <p className="hpa-notifications__state">{t("Loading...")}</p>}

            {!loading && error && (
              <p className="hpa-notifications__state">
                {t("Could not load your notifications. Please try again.")}
              </p>
            )}

            {!loading && !error && items.length === 0 && (
              <p className="hpa-notifications__state">{t("You have no notifications yet.")}</p>
            )}

            {!loading &&
              !error &&
              items.map((item) => {
                const internalPath = toInternalPath(item.action_url);

                return (
                  <article
                    key={item.id}
                    className={`hpa-notifications__item${item.read_at ? "" : " is-unread"}`}
                  >
                    <span className="hpa-notifications__icon" aria-hidden="true">
                      <i className={ICONS[item.icon] || ICONS.bell} />
                    </span>

                    <div className="hpa-notifications__content">
                      <h4>{item.title}</h4>
                      {item.body && <p>{item.body}</p>}

                      {item.action_url &&
                        (internalPath ? (
                          <Link
                            to={internalPath}
                            className="hpa-notifications__action"
                            onClick={() => {
                              handleRead(item);
                              setOpen(false);
                              onNavigate?.();
                            }}
                          >
                            {item.action_label || t("Open")}
                          </Link>
                        ) : (
                          <a
                            href={item.action_url}
                            target="_blank"
                            rel="noreferrer"
                            className="hpa-notifications__action"
                            onClick={() => handleRead(item)}
                          >
                            {item.action_label || t("Open")}
                          </a>
                        ))}

                      <div className="hpa-notifications__meta">
                        <time dateTime={item.created_at}>
                          {relativeTime(item.created_at, isArabic)}
                        </time>
                        {!item.read_at && (
                          <button
                            type="button"
                            className="hpa-notifications__link"
                            onClick={() => handleRead(item)}
                          >
                            {t("Mark as read")}
                          </button>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      className="hpa-notifications__dismiss"
                      onClick={(event) => handleDelete(event, item.id)}
                      aria-label={t("Dismiss")}
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                  </article>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
